import { AnnouncementBar, AppButton } from '@clickguard/design-system'
import { useAccountStore, useUserStore } from 'store'
import { useBlackFridayOffer } from './use-black-friday-offer'
import { ClaimOfferModal } from './claim-offer-modal'

const ribbon = (y: number, amp: number, thick: number, phase: number) => {
	const xs = Array.from({ length: 46 }, (_, i) => -120 + i * 24)
	const edge = (x: number, off: number) =>
		(
			y +
			off +
			amp * Math.sin(x / 140 + phase) +
			(amp / 3) * Math.sin(x / 55 + phase * 2)
		).toFixed(1)
	const top = xs.map((x) => `${x} ${edge(x, -thick / 2)}`)
	const bottom = xs
		.slice()
		.reverse()
		.map((x) => `${x} ${edge(x, thick / 2 + (thick / 3) * Math.sin(x / 90))}`)
	return `M${top.join(' L')} L${bottom.join(' L')} Z`
}

const RIBBONS = [
	ribbon(170, 22, 26, 0.4),
	ribbon(245, 30, 18, 2.1),
	ribbon(300, 18, 34, 4.0),
	ribbon(365, 26, 14, 1.2),
	ribbon(430, 20, 28, 3.3)
]

export const BlackFridayBackdrop = ({ ribbonOpacity = 0.16 }: { ribbonOpacity?: number } = {}) => (
	<div
		aria-hidden="true"
		className="pointer-events-none absolute inset-0 bg-[rgb(var(--cg-brand))]"
	>
		<svg
			viewBox="0 0 800 600"
			preserveAspectRatio="xMidYMid slice"
			className="absolute inset-0 h-full w-full"
		>
			<g fill="#fff" opacity={ribbonOpacity} transform="rotate(-18 400 300)">
				{RIBBONS.map((d) => (
					<path key={d.slice(0, 24)} d={d} />
				))}
			</g>
		</svg>
	</div>
)

export const BlackFridayBanner = ({
	accountId,
	userId,
	onHeightChange
}: {
	accountId: string
	userId: string
	onHeightChange: (px: number) => void
}) => {
	const campaign = useBlackFridayOffer(accountId, userId)
	const account = useAccountStore((state) => state.account)
	const user = useUserStore((state) => state.user)
	const access = account?.user_access.find(
		(item) => item.user_id === user?.id
	)?.access_level
	const canClaim =
		(access === 'OWNER' || access === 'WRITE') &&
		campaign.offer?.can_claim === true
	const offer = campaign.reviewedOffer || campaign.offer

	return (
		<>
			<AnnouncementBar
				open={campaign.visible}
				className="shrink-0"
				aria-label="Black Friday offer"
				label="Black Friday · Pre-sale"
				backdrop={<BlackFridayBackdrop />}
				dismissible
				dismissLabel="Dismiss the Black Friday offer"
				onDismiss={campaign.dismiss}
				onHeightChange={onHeightChange}
				action={
					<AppButton
						size="sm"
						variant="marketing"
						label={campaign.pending ? 'Check claim status' : 'Claim this offer'}
						onClick={campaign.openModal}
						aria-haspopup="dialog"
						aria-expanded={campaign.modalOpen}
					/>
				}
			>
				Switch to annual and get <s className="opacity-60">20%</s>{' '}
				<strong className="font-medium">35% off</strong> your first year, plus
				3x ad spend limits through Q4.{' '}
				<a
					href={campaign.offer?.details_url}
					target="_blank"
					rel="noopener noreferrer"
				>
					See offer details
				</a>
			</AnnouncementBar>
			<ClaimOfferModal
				open={campaign.modalOpen}
				onClose={campaign.closeModal}
				quote={offer?.quote || null}
				detailsUrl={offer?.details_url || ''}
				onClaim={campaign.claim}
				onRefresh={campaign.refresh}
				onRetry={campaign.retryPendingClaim}
				canRetry={campaign.canRetryPending}
				claimBlockedReason={campaign.offer?.claim_blocked_reason || null}
				canClaim={canClaim}
				available={!!campaign.offer?.eligible && !campaign.expired}
				pending={campaign.pending}
				submitting={campaign.submitting}
				refreshing={campaign.refreshing}
				error={campaign.error}
			/>
		</>
	)
}
